import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import CustomButton from "../common/CustomButton";
import Search from "../assets/images/search.png";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CustomTextField from "../common/CustomTextFeild";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import CustomModal from "../common/CustomModal";
import CustomSelect from "../common/CustomSelect";
import { _apiUrls } from "../utils/endpoints/apiUrls";
import { apiRequest } from "../utils/config/apiRequest";
import moment from "moment";
import Pagination from "../common/Pagination";
import CustomTooltip from "../common/CustomTooltip";
import { getTheme } from "../redux/selectors/appSelectors";
import { useSelector } from "react-redux";
import WhiteSearch from "../assets/images/whitesearch.png";

export default function NotificationManagement() {
  const theme = useSelector(getTheme);
  const isDarkMode = theme === "dark";

  const navigate = useNavigate();
  const [filterModal, setFilterModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState({ rows: [] });
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;

  // Filter States -----------------------------------------------

  const [filterOptions, setFilterOptions] = useState({
    name: [],
    country: [],
    category: [],
    createDate: [],
    backgroundColor: [],
  });

  const [filters, setFilters] = useState({
    name: "",
    country: "",
    category: "",
    createDate: "",
    backgroundColor: "",
  });
  const [filtersCleared, setFiltersCleared] = useState(false);

  const fetchNotification = async () => {
    setLoading(true);
    const { data, status } = await apiRequest({
      endUrl: _apiUrls.getNotification,
      method: "GET",
      query: {
        search: searchText,
        page: currentPage,
        limit: itemsPerPage,
        ...filters,
      },
    });
    if (status) {
      setState({ ...state, rows: Array.isArray(data?.rows) ? data.rows : [] });
      setTotalItems(data?.totalCount || 0);
    }
    setLoading(false);
  };

  // Delete Notification -----------------------------------------

  const [modalShow, setModalShow] = useState(false);
  const [modalState, setModalState] = useState({ name: "", notifyuser: null });

  const handleModalAction = (type, notifyuser) => {
    setModalState({ name: type, notifyuser });
    setModalShow(true);
  };

  const confirmDelete = async () => {
    const notifyuser = modalState?.notifyuser;
    if (!notifyuser) return;
    const { status } = await apiRequest({
      endUrl: _apiUrls.deleteNotification,
      method: "DELETE",
      query: { notificationId: notifyuser._id },
      showMsg: true,
    });
    if (status) {
      fetchNotification();
    }
    setModalShow(false);
    setModalState({ name: "", notifyuser: null });
  };

  // Filter Fucntions -----------------------------------------------

  const fetchFilterOptions = async () => {
    const types = [
      "name",
      "country",
      "category",
      "createDate",
      "backgroundColor",
    ];
    const results = {};
    for (const type of types) {
      const { data, status } = await apiRequest({
        endUrl: _apiUrls.filterNotification,
        method: "GET",
        query: { filterType: type },
      });

      if (status && Array.isArray(data?.rows)) {
        results[type] = data.rows.map((val) => ({
          label: val?.toString(),
          value: val?.toString(),
        }));
      } else {
        results[type] = [];
      }
    }

    setFilterOptions(results);
  };

  const clearFilters = () => {
    setFilters({
      name: "",
      country: "",
      category: "",
      createDate: "",
      backgroundColor: "",
    });
    setFiltersCleared(true); // ✅ mark that filters were cleared
  };

  useEffect(() => {
    fetchNotification();
  }, [searchText, currentPage]);

  useEffect(() => {

    if (filtersCleared) {
      fetchNotification(); // ✅ run fetch after filters are cleared
      setFiltersCleared(false); // reset flag
    }
  }, [filtersCleared]);


  return (
    <>
      <Row>
        <Col md={12} className="py-3">
          <h4 className="dashheading">Manage Notification</h4>
          <p className="dashsubheading">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            list
          </p>
        </Col>
      </Row>

      <Row>
        <Col md={9} xs={12} className="pb-3">
          <div className="scroll-buttons-container">
            <div className="d-flex gap-3">
              <CustomButton
                variant="contained"
                text={"Add More"}
                iconLeft={<PersonAddIcon style={{ fontSize: 18 }} />}
                className="containebtn"
                onClick={() => navigate("/dashboard/addnotification")}
              />

              <CustomButton
                variant="contained"
                text="Filter"
                iconLeft={<FilterAltRoundedIcon style={{ fontSize: 18 }} />}
                className="containebtn"
                onClick={() => {
                  setFilterModal(true);
                  fetchFilterOptions();
                }}
              />

              <CustomButton
                variant="outline2"
                onClick={clearFilters}
                text="Clear Filters"
                className="outline2-btn"
              />
            </div>
          </div>
        </Col>

        <Col md={3} xs={12} className="pb-3">
          <CustomTextField
            style={{
              padding: " 7px 13px",
              borderRadius: "10px",
              height: "auto",
            }}
            icon={isDarkMode ? WhiteSearch : Search}
            iconHeight="15px"
            placeholder="Search Content"
            value={searchText}
            // onChange={(e) => setSearchText(e.target.value)}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              if (value === "") {
                setCurrentPage(1); // ✅ reset to first page on clear
              }
            }}
          />
        </Col>

        <Col md={12} className="mt-2">
          <div className="head-border" />
        </Col>

        <Col md={12} className="mt-4 pb-2">
          <div className="table-responsive">
            <table className="admintable">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Push Notice Title</th>
                  <th>Push Notice Description</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Push Notice Address</th>
                  <th>Push Notice Category</th>
                  <th>Push Notice Background Color</th>
                  <th>Push Notice logo</th>
                  <th>Push Notice Date & Time</th>
                  <th>Push Notice Expiry Date</th>
                  <th>Push Notice Background Image</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="14" className="text-center py-4">
                      <div className="d-flex justify-content-center align-items-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : state.rows.length > 0 ? (
                  state.rows.map((item, index) => (
                    <tr key={item?._id || index}>
                      <td>{index + 1}</td>
                      <td>{item?.notificationTitle || "----"}</td>
                      <td>{item?.description || "----"}</td>
                      <td>{item?.user?.fullName || "----"}</td>
                      <td>{item?.user?.email || "----"}</td>
                      <td>{item?.user?.mobile || "----"}</td>
                      <td>
                        {item?.address || "----"}
                        {/* <a
                            href={`https://www.google.com/maps?q=${item.geolocation.coordinates[1]},${item.geolocation.coordinates[0]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on Map
                          </a> */}
                      </td>
                      <td>{item?.categoryId?.categoryName || "----"}</td>
                      <td>
                        <div
                          className="choose-color"
                          style={{
                            backgroundColor: item?.backgroundColor,
                          }}
                        ></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          className=""
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                          }}
                        >
                          {item?.logo && (
                            <>
                              {(() => {

                                const fullImageUrl = item?.logo?.startsWith("http")
                                  ? item.logo
                                  : `${process.env.REACT_APP_API_ORIGIN}/${item.logo.replace(/^\/+/, "")}`;

                                return (
                                  <img
                                    src={fullImageUrl}
                                    alt=""
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "contain",
                                      display: "block",
                                      margin: "0 auto",
                                    }}
                                  />
                                );
                              })()}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {moment(item?.startDate).format("DD/MM/YYYY H:mm") ||
                          "----"}
                      </td>
                      <td>
                        {moment(item?.endDate).format("DD/MM/YYYY") || "----"}
                      </td>
                      <td>
                        <div
                          className=""
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                          }}
                        >
                          {item?.bannerImage ? (
                            <>
                              {(() => {
                                const fullImageUrl = `${process.env.REACT_APP_API_ORIGIN}/${item?.bannerImage?.replace(/^\/+/, "")}`;

                                return (
                                  <img
                                    src={fullImageUrl}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: "78px",
                                      textAlign: "center",
                                    }}
                                  />
                                );
                              })()}
                            </>
                          ) : (
                            <div className="text-center">
                              <p className="mb-0 no-data-found">-----</p>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="gap-2 d-flex align-items-center">
                          <CustomTooltip title="Edit">
                            <p
                              className="mb-0"
                              onClick={() =>
                                navigate(
                                  `/dashboard/addnotification/${item?._id}`
                                )
                              }
                              style={{ textDecoration: "none" }}
                            >
                              <EditIcon
                                sx={{
                                  color: isDarkMode
                                    ? "rgba(75, 128, 235, 1)"
                                    : "#2C2C2C",
                                  cursor: "pointer",
                                  fontSize: 18,
                                }}
                              />
                            </p>
                          </CustomTooltip>

                          <CustomTooltip title="Delete">
                            <DeleteIcon
                              onClick={() => handleModalAction("delete", item)}
                              sx={{
                                color: "#F57171",
                                cursor: "pointer",
                                fontSize: 18,
                              }}
                            />
                          </CustomTooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14" className="text-center py-4">
                      <p className="textwhite f-14 mb-0">No records found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Col>

        <Col md={12} className="text-end mt-4">
          <div className="d-flex justify-content-end">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </Col>
      </Row>

      {/* Filter Modal --------------------------------- */}

      <CustomModal
        show={filterModal}
        onHide={() => {
          setFilterModal(false);
        }}
        title="List of Content"
        subtitle="Filter"
        showCloseButton={true}
        footerButtons={
          <>
            <div
              style={{
                display: "flex",
                marginTop: 10,
                justifyContent: "flex-start",
                gap: "10px",
                width: "100%",
              }}
            >
              <Button
                variant="secondary"
                style={{
                  backgroundColor: "rgba(75, 128, 235, 1)",
                  borderColor: "rgba(75, 128, 235, 1)",
                  padding: "8px 30px",
                  borderRadius: 10,
                }}
                onClick={() => {
                  setFilterModal(false);
                  fetchNotification();
                }}
              >
                Apply Filters
              </Button>
            </div>
          </>
        }
      >
        <Row className="mt-3">
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select Name</p>
            <CustomSelect
              options={filterOptions.name}
              value={filters.name}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, name: option.value }))
              }
            />
          </Col>
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select Country</p>
            <CustomSelect
              options={filterOptions.country}
              value={filters.country}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, country: option.value }))
              }
            />
          </Col>
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select Category</p>
            <CustomSelect
              options={filterOptions.category}
              value={filters.category}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, category: option.value }))
              }
            />
          </Col>
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select Creation Date</p>
            <CustomSelect
              options={filterOptions.createDate}
              value={filters.createDate}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, createDate: option.value }))
              }
            />
          </Col>
          <Col md={12}>
            <p className="mb-1 input-label f-14">Select Background Color</p>
            <CustomSelect
              options={filterOptions.backgroundColor}
              value={filters.backgroundColor}
              onChange={(option) =>
                setFilters((prev) => ({
                  ...prev,
                  backgroundColor: option.value,
                }))
              }
            />
          </Col>
        </Row>
      </CustomModal>

      {/* Delete Modal ------------------------------------ */}

      <CustomModal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setModalState({ name: "", notifyuser: null });
        }}
        title={
          modalState.name === "delete" ? "Confirm Deletion" : "User Details"
        }
        showCloseButton={true}
        footerButtons={
          <>
            {modalState.name === "delete" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <Button
                  variant="secondary"
                  style={{ padding: "5px 30px" }}
                  onClick={() => {
                    setModalShow(false);
                    setModalState({ name: "", notifyuser: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  style={{ padding: "5px 30px" }}
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </div>
            )}
          </>
        }
      >
        {modalState?.name === "delete" && (
          <p className="my-4 text-center textwhite" style={{ fontSize: 18 }}>
            Are you sure you want to delete{" "}
            <strong>{modalState?.notifyuser?.notificationTitle}</strong>?
          </p>
        )}
      </CustomModal>
    </>
  );
}
